import { ExcelEvents, Workbook } from "./excelEvents";
import { WorksheetClassificationEvents } from "./worksheetClassification";
import { Artifact, ArtifactStore } from "./artifacts";
import { AppEventBus } from "./appEventBus";
import { WorkbookClassificationEvents } from "./workbookClassification";
import { ProgressiveEnhancementEvents } from "./progressiveEnhancement";

export type CellValue = string | number | boolean | null;
export type RangeValue = CellValue | CellValue[][];

export type ExtractionContext = {
    workbookId: string;
    worksheetId: string;
    workbookNamedRanges: Record<string, RangeValue>;
    worksheetNamedRanges: Record<string, RangeValue>;
}

export type WorksheetExtractionRule = {
    id: string;
    name: string;
    targetSchemaId: string;
    ranges?: Record<string, string>;
    extract?: (ctx: ExtractionContext) => Promise<Artifact[]>;
}

export type WorkbookExtractionRuleSet = {
    readonly workbookCode: string;
    readonly worksheets: Record<string, WorksheetExtractionRule[]>;
}

export type WorkbookExtractionRuleFetcher = (workbookCode: string) => Promise<WorkbookExtractionRuleSet>;

export class WorksheetExtractor {
    private readonly classifiedWorksheets: Map<string, string> = new Map(); // worksheetId -> classificationCode
    private worksheetExtractionRuleSet:WorkbookExtractionRuleSet;
    private workbook:Workbook;

    constructor(
        private readonly appEventBus:AppEventBus,
        private readonly artifactStore: ArtifactStore,
        private readonly workbookExtractionRuleFetcher: WorkbookExtractionRuleFetcher
    ) {
        this.appEventBus.namespaces.excel.events.workbookOpened.on(this.handleWorkbookOpened.bind(this));
        this.appEventBus.namespaces.workbookClassification.events.workbookClassified.on(this.handleWorkbookClassified.bind(this));
        this.appEventBus.namespaces.progressiveEnhancement.events.worksheetProcessed.on(this.handleWorksheetProcessed.bind(this));
        this.appEventBus.namespaces.worksheetClassification.events.worksheetUnclassified.on(this.handleWorksheetUnclassified.bind(this));
        this.appEventBus.namespaces.excel.events.worksheetChanged.on(this.handleWorksheetChanged.bind(this));
    }

    private handleWorkbookOpened(payload: ExcelEvents['workbookOpened']) {
        this.workbook = payload.workbook;
    }

    private async handleWorkbookClassified(payload: WorkbookClassificationEvents['workbookClassified']) {
        this.worksheetExtractionRuleSet = await this.workbookExtractionRuleFetcher(payload.code);
    }

    private async handleWorksheetProcessed(payload: ProgressiveEnhancementEvents['worksheetProcessed']) {
        this.classifiedWorksheets.set(payload.worksheetId, payload.worksheetCode);
        await this.runExtraction(payload.workbookId, payload.worksheetId, payload.worksheetCode);
    }

    private async handleWorksheetUnclassified(payload: WorksheetClassificationEvents['worksheetUnclassified']) {
        this.classifiedWorksheets.delete(payload.worksheetId);
        // Destroy all artifacts descending from this worksheet
        const descendants = await this.artifactStore.getAllDescendants(`wb_${payload.workbookId}_ws_${payload.worksheetId}`);
        for (const artifact of descendants) {
            this.artifactStore.destroy(artifact.id);
        }
    }

    private async handleWorksheetChanged(payload: ExcelEvents['worksheetChanged']) {
        const classificationCode = this.classifiedWorksheets.get(payload.worksheetId);
        if (classificationCode) {
            await this.runExtraction(payload.workbookId, payload.worksheetId, classificationCode);
        }
    }

    private async runExtraction(workbookId: string, worksheetId: string, classificationCode: string) {
        const rules = this.worksheetExtractionRuleSet.worksheets[classificationCode];
        if (!rules || rules.length === 0) {
            return;
        }

        try {
            const {workbookNamedRanges, worksheetNamedRanges} = await Excel.run(async (context) => {
                const workbook = context.workbook;
                const workbookNamedItems = workbook.names;
                const allWorkbookNamedItems = workbookNamedItems.load('items/name,items/scope,items/type');
                const worksheet = workbook.worksheets.getItem(worksheetId);
                const worksheetNamedItems = worksheet.names;
                const allNamedRanges = worksheetNamedItems.load('items/name,items/scope,items/type');

                await context.sync();

                const workbookNamedRanges = allWorkbookNamedItems.items.filter(namedItem => namedItem.type === "Range").reduce<Record<string,Excel.Range>>(
                    (acc, namedRange) => {
                        acc[namedRange.name] = namedRange.getRange().load('values');
                        return acc;
                    },
                    {}
                );
                const worksheetNamedRanges = allNamedRanges.items.filter(namedItem => namedItem.type === "Range").reduce<Record<string,Excel.Range>>(
                    (acc, namedRange) => {
                        acc[namedRange.name] = namedRange.getRange().load('values');
                        return acc;
                    },
                    {}
                );

                await context.sync();

                const workbookNamedRangesValues = Object.entries(workbookNamedRanges).reduce<Record<string, RangeValue>>((acc, [name, range]) => {
                    acc[name] = range.values && range.values.length === 1 && range.values[0].length === 1 ? range.values[0][0] : range.values;
                    return acc;
                }, {});

                const worksheetNamedRangesValues = Object.entries(worksheetNamedRanges).reduce<Record<string, RangeValue>>((acc, [name, range]) => {
                    acc[name] = range.values && range.values.length === 1 && range.values[0].length === 1 ? range.values[0][0] : range.values;
                    return acc;
                }, {});

                console.log("all named things during worksheet extraction", workbookNamedRangesValues, worksheetNamedRangesValues);

                return {
                    workbookNamedRanges: workbookNamedRangesValues,
                    worksheetNamedRanges: worksheetNamedRangesValues,
                }
            });

            const workbookArtifactId = `wb-${workbookId}`;
            const worksheetArtifactId = `ws-${worksheetId}`;

            const worksheetArtifact: Artifact = {
                id: worksheetArtifactId,
                type: "schema.worksheet",
                correlationIDs: [], //TODO: correlation IDs
                properties: {
                    workbookId: workbookId,
                    worksheetId: worksheetId,
                    workbookCode: this.worksheetExtractionRuleSet.workbookCode,
                    worksheetCode: classificationCode,
                    worksheetName: this.workbook.worksheets.find(ws => ws.id === worksheetId)?.name,
                    namedRanges: worksheetNamedRanges,
                },
                artifacts: [],
                dependencies: [
                    {
                        artifactId: workbookArtifactId,
                        type: "parent",
                        properties: {}
                    }
                ]
            };

            const workbookArtifact:Artifact = {
                id: workbookArtifactId,
                type: "schema.workbook",
                correlationIDs: [ ], //TODO: correlation IDs
                properties: {
                    workbookId: workbookId,
                    workbookCode: this.worksheetExtractionRuleSet.workbookCode,
                    workbookName: this.workbook.name,
                    workbookUrl: this.workbook.url,
                    namedRanges: workbookNamedRanges,
                },
                artifacts: [
                    worksheetArtifact
                ],
                dependencies: []
            }

            this.artifactStore.upsert(workbookArtifact);

            let allUpsertedIds: string[] = [];
            for (const rule of rules) {
                try {
                    const result = await rule.extract({
                        workbookNamedRanges,
                        worksheetNamedRanges,
                        workbookId,
                        worksheetId
                    });
                    
                    // Result could be a single root object or an array
                    const results = Array.isArray(result) ? result : [result];
                    
                    for(const r of results) {
                        const ids = await this.artifactStore.upsert(r);
                        allUpsertedIds.push(...ids);
                    }

                } catch (e) {
                    console.error(`Error running extraction rule ${rule.name}:`, e);
                }
            }

            
            // Sync with store: Remove artifacts that were previously associated with this worksheet
            // but were NOT returned in this extraction run.
            // We find all descendants of the worksheet "virtual" artifact
            const existingArtifacts = await this.artifactStore.getAllDescendants(`ws-${worksheetId}`);
            const newArtifactIds = new Set(allUpsertedIds);
            
            for (const existing of existingArtifacts) {
                if (!newArtifactIds.has(existing.id)) {
                    this.artifactStore.destroy(existing.id);
                }
            }
        } catch (e) {
            console.error("Error running worksheet extraction:", e);
        }
    }
}
